/**
 * Customer Intel Radar
 *
 * A visual radar-style display showing customer priority score with badges:
 * - High-Value (order > R500)
 * - Generous Tipper (tip > 10%)
 * - Loyalty Member (has coupons)
 * - VIP indicators
 */

import React, { useMemo } from 'react';
import { Box, Typography, Stack, Tooltip, keyframes } from '@mui/material';
import { useMissionTheme } from '../theme';

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 10px currentColor;
  }
  50% {
    box-shadow: 0 0 25px currentColor, 0 0 50px currentColor;
  }
`;

const radarScan = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

interface CustomerIntelRadarProps {
  orderTotal: number;
  driverTip: number;
  coupons?: any[];
  promotions?: any[];
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    totalOrders?: number;
  };
  /** Override for totalOrders - use dynamically fetched value instead of user.totalOrders */
  customerTotalOrders?: number;
}

interface Badge {
  icon: string;
  color: string;
  label: string;
  active: boolean;
}

export default function CustomerIntelRadar({
  orderTotal: orderTotalRaw,
  driverTip: driverTipRaw,
  coupons,
  promotions,
  user,
  customerTotalOrders,
}: CustomerIntelRadarProps) {
  const { mc, badges } = useMissionTheme();

  // Ensure numeric values (API may return strings)
  const orderTotal = Number(orderTotalRaw) || 0;
  const driverTip = Number(driverTipRaw) || 0;

  // Calculate badges
  const computedBadges = useMemo((): Badge[] => {
    const result: Badge[] = [];

    // High Value
    const isHighValue = orderTotal >= badges.highValue.threshold;
    result.push({
      icon: badges.highValue.icon,
      color: badges.highValue.color,
      label: `High Value (R${orderTotal.toFixed(0)})`,
      active: isHighValue,
    });

    // Generous Tipper
    const tipPercent = orderTotal > 0 ? driverTip / orderTotal : 0;
    const isGenerousTipper = tipPercent >= badges.generousTipper.threshold;
    result.push({
      icon: badges.generousTipper.icon,
      color: badges.generousTipper.color,
      label: `Generous Tipper (${(tipPercent * 100).toFixed(0)}%)`,
      active: isGenerousTipper,
    });

    // Loyalty Member
    const hasLoyalty = (coupons && coupons.length > 0) || (promotions && promotions.length > 0);
    result.push({
      icon: badges.loyaltyMember.icon,
      color: badges.loyaltyMember.color,
      label: 'Loyalty Member',
      active: hasLoyalty || false,
    });

    // Repeat Customer vs First Order
    // Prefer dynamically fetched customerTotalOrders over stale user.totalOrders
    const totalOrders = customerTotalOrders ?? user?.totalOrders ?? 0;
    if (totalOrders >= badges.repeatCustomer.threshold) {
      result.push({
        icon: badges.repeatCustomer.icon,
        color: badges.repeatCustomer.color,
        label: `Repeat Customer (${totalOrders} orders)`,
        active: true,
      });
    } else if (totalOrders <= 1) {
      result.push({
        icon: badges.firstOrder.icon,
        color: badges.firstOrder.color,
        label: 'First Order - Extra Care!',
        active: true,
      });
    }

    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderTotal, driverTip, coupons, promotions, customerTotalOrders, user?.totalOrders, badges]);

  // Calculate priority score (0-100)
  const priorityScore = useMemo(() => {
    let score = 0;

    // Order value contribution (max 40 points)
    score += Math.min(40, (orderTotal / 1000) * 40);

    // Tip contribution (max 20 points)
    const tipPercent = orderTotal > 0 ? driverTip / orderTotal : 0;
    score += Math.min(20, tipPercent * 100);

    // Loyalty contribution (20 points)
    if ((coupons && coupons.length > 0) || (promotions && promotions.length > 0)) {
      score += 20;
    }

    // Repeat customer contribution (20 points)
    // Prefer dynamically fetched customerTotalOrders over stale user.totalOrders
    const totalOrders = customerTotalOrders ?? user?.totalOrders ?? 0;
    score += Math.min(20, totalOrders * 4);

    return Math.min(100, Math.round(score));
  }, [orderTotal, driverTip, coupons, promotions, customerTotalOrders, user?.totalOrders]);

  // Get score color
  const getScoreColor = (score: number): string => {
    if (score >= 80) return mc.gold;
    if (score >= 60) return mc.success;
    if (score >= 40) return mc.secondary;
    return mc.textSecondary;
  };

  const scoreColor = getScoreColor(priorityScore);
  const activeBadges = computedBadges.filter((b) => b.active);

  return (
    <Box
      sx={{
        p: 2,
        background: mc.glass,
        backdropFilter: 'blur(12px)',
        borderRadius: 3,
        border: `1px solid ${mc.glassBorder}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" spacing={3} alignItems="center">
        {/* Radar Display */}
        <Box
          sx={{
            width: 100,
            height: 100,
            position: 'relative',
            flexShrink: 0,
          }}
        >
          {/* Radar Background */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: `2px solid ${mc.border}`,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: '20%',
                borderRadius: '50%',
                border: `1px solid ${mc.border}`,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: '40%',
                borderRadius: '50%',
                border: `1px solid ${mc.border}`,
              },
            }}
          />

          {/* Radar Scan Line */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '50%',
                height: '2px',
                background: `linear-gradient(90deg, ${scoreColor}, transparent)`,
                transformOrigin: 'left center',
                animation: `${radarScan} 4s linear infinite`,
              },
            }}
          />

          {/* Score Display */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontFamily: 'monospace',
                color: scoreColor,
                textShadow: `0 0 10px ${scoreColor}`,
                lineHeight: 1,
              }}
            >
              {priorityScore}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: mc.textMuted,
                fontSize: '0.6rem',
                letterSpacing: 1,
              }}
            >
              PRIORITY
            </Typography>
          </Box>
        </Box>

        {/* Customer Info & Badges */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: mc.textMuted,
              letterSpacing: 1,
              fontSize: '0.65rem',
              mb: 0.5,
            }}
          >
            CUSTOMER INTEL
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: mc.textPrimary,
              fontWeight: 600,
              mb: 1.5,
            }}
          >
            {user?.firstName || 'Unknown'} {user?.lastName || 'Customer'}
          </Typography>

          {/* Active Badges */}
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {activeBadges.map((badge, index) => (
              <Tooltip key={index} title={badge.label} arrow>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    bgcolor: `${badge.color}15`,
                    border: `1px solid ${badge.color}40`,
                    color: badge.color,
                    animation: `${pulseGlow} 3s ease-in-out infinite`,
                    cursor: 'help',
                  }}
                >
                  <span style={{ fontSize: '1rem' }}>{badge.icon}</span>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  >
                    {badge.label.split(' ')[0]}
                  </Typography>
                </Box>
              </Tooltip>
            ))}

            {activeBadges.length === 0 && (
              <Typography variant="caption" sx={{ color: mc.textMuted }}>
                Standard customer profile
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
